import { useState, useEffect } from 'react';
import { X, Search, Filter } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { medicationsService } from '@/services/api/medications.service';
import type { Medication } from '@/types/consultation';

interface MedicationSearchModalProps {
  onClose: () => void;
  onSelect: (medication: Medication) => void;
  selectedMedicationIds: string[];
}

export function MedicationSearchModal({
  onClose,
  onSelect,
  selectedMedicationIds,
}: MedicationSearchModalProps) {
  const [medications, setMedications] = useState<Medication[]>([]);
  const [filteredMedications, setFilteredMedications] = useState<Medication[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Filtros
  const [minDosage, setMinDosage] = useState<number | ''>('');
  const [maxDosage, setMaxDosage] = useState<number | ''>('');
  const [frequency, setFrequency] = useState<number | ''>('');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadMedications();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [medications, searchTerm, minDosage, maxDosage, frequency]);

  const loadMedications = async () => {
    setIsLoading(true);
    try {
      const meds = await medicationsService.getAll();
      setMedications(meds);
    } catch (error) {
      toast.error('Erro ao carregar medicamentos');
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = medications;

    // Filtro de busca por nome
    if (searchTerm) {
      filtered = filtered.filter((med) =>
        med.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtro de dosagem mínima
    if (minDosage !== '') {
      filtered = filtered.filter((med) => med.dosageMg >= minDosage);
    }

    // Filtro de dosagem máxima
    if (maxDosage !== '') {
      filtered = filtered.filter((med) => med.dosageMg <= maxDosage);
    }

    // Filtro de frequência
    if (frequency !== '') {
      filtered = filtered.filter((med) => med.frequencyHours === frequency);
    }

    setFilteredMedications(filtered);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setMinDosage('');
    setMaxDosage('');
    setFrequency('');
  };

  const handleSelect = (medication: Medication) => {
    onSelect(medication);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 backdrop-blur-sm bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-semibold text-gray-900">Buscar Medicamento</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Search Bar */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por nome do medicamento..."
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoFocus
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-3 rounded-lg flex items-center gap-2 transition-colors ${
                showFilters
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Filter className="h-5 w-5" />
              Filtros
            </button>
          </div>

          {/* Filters */}
          {showFilters && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-3">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dosagem Mínima (mg)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={minDosage}
                    onChange={(e) =>
                      setMinDosage(e.target.value ? parseFloat(e.target.value) : '')
                    }
                    placeholder="Ex: 5"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dosagem Máxima (mg)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={maxDosage}
                    onChange={(e) =>
                      setMaxDosage(e.target.value ? parseFloat(e.target.value) : '')
                    }
                    placeholder="Ex: 500"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Frequência (horas)
                  </label>
                  <select
                    value={frequency}
                    onChange={(e) =>
                      setFrequency(e.target.value ? parseInt(e.target.value) : '')
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">Todas</option>
                    <option value="4">4h</option>
                    <option value="6">6h</option>
                    <option value="8">8h</option>
                    <option value="12">12h</option>
                    <option value="24">24h</option>
                  </select>
                </div>
              </div>
              <button
                onClick={handleClearFilters}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Limpar filtros
              </button>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">Carregando medicamentos...</div>
            </div>
          ) : filteredMedications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <Search className="h-12 w-12 mb-2 opacity-50" />
              <p className="text-lg">Nenhum medicamento encontrado</p>
              <p className="text-sm">Tente ajustar os filtros ou a busca</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3">
              {filteredMedications.map((med) => {
                const isSelected = selectedMedicationIds.includes(med.id);
                return (
                  <button
                    key={med.id}
                    onClick={() => !isSelected && handleSelect(med)}
                    disabled={isSelected}
                    className={`p-4 border-2 rounded-lg text-left transition-all ${
                      isSelected
                        ? 'border-green-500 bg-green-50 cursor-not-allowed opacity-60'
                        : 'border-gray-200 hover:border-blue-500 hover:bg-blue-50'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg text-gray-900">{med.name}</h3>
                        <div className="mt-2 flex items-center gap-4 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <span className="font-medium">Dosagem:</span>
                            <span className="text-blue-600 font-semibold">{med.dosageMg}mg</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="font-medium">Frequência:</span>
                            <span className="text-blue-600 font-semibold">
                              {med.frequencyHours}h
                            </span>
                          </div>
                        </div>
                      </div>
                      {isSelected && (
                        <span className="ml-4 px-3 py-1 bg-green-600 text-white text-xs font-semibold rounded-full">
                          Selecionado
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-between items-center text-sm text-gray-600">
            <span>
              {filteredMedications.length} medicamento(s) encontrado(s)
            </span>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-medium"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
